import { CrudService, type CrudRawResult } from "@/services/crudService";
import type { Group, Session } from "@/types/domain";

export interface FetchGroupsResponse {
  groups: Group[];
  memberGroupIds: string[];
}

export interface CreateGroupPayload {
  isPublic: boolean;
  diseaseKey?: string;
  name?: string;
  description?: string;
  language: string;
}

export interface CreateSessionPayload {
  dateTime: string;
  timeZone: string;
  description?: string;
  maxAttendees?: number;
}

const crud = new CrudService();

// Error con forma axios-like ({ response: { status, data } }) para que errorHandler
// (getApiErrorStatus / extractApiErrorMessage) siga funcionando sin cambios.
function apiError(status: number, data: unknown) {
  const err = new Error(`Request failed with status ${status}`) as Error & {
    response: { status: number; data: unknown };
  };
  err.response = { status, data };
  return err;
}

async function send<T>(
  method: "GET" | "POST" | "DELETE",
  url: string,
  body?: unknown,
): Promise<T> {
  const opts = { raw: true } as const;
  const res = (await (method === "GET"
    ? crud.httpGet(url, undefined, undefined, "json", opts)
    : method === "POST"
    ? crud.httpPost(url, body, undefined, "json", opts)
    : crud.httpDelete(url, undefined, undefined, "json", opts))) as CrudRawResult<T>;
  if (!res.ok) {
    throw apiError(res.status, res.data);
  }
  return res.data as T;
}

export async function fetchGroups(language: string): Promise<FetchGroupsResponse> {
  const res = await send<FetchGroupsResponse>("GET", `/livegroups/get-groups?language=${language}`);
  const groups = (res.groups ?? []).map(g => ({ ...g, id: String(g.id) }));
  const memberGroupIds = (res.memberGroupIds ?? []).map(id => String(id));
  return { groups, memberGroupIds };
}

export async function fetchSessions(groupId: string): Promise<Session[]> {
  const res = await send<{ sessions: Session[] }>("GET", `/livegroups/${groupId}/get-sessions`);
  return res.sessions ?? [];
}

export async function getGroupBySlug(slug: string): Promise<Group> {
  const res = await send<Group>("GET", `/livegroups/by-slug/${slug}`);
  return { ...res, id: String(res.id) };
}

export async function joinGroup(groupId: string): Promise<void> {
  await send<void>("POST", `/livegroups/${groupId}/join-group`);
}

export async function leaveGroup(groupId: string): Promise<void> {
  await send<void>("DELETE", `/livegroups/${groupId}/leave-group`);
}

export async function createGroup(payload: CreateGroupPayload): Promise<Group> {
  const res = await send<Group>("POST", "/livegroups/create-group", payload);
  return { ...res, id: String(res.id) };
}

export async function createSession(groupId: string, payload: CreateSessionPayload): Promise<Session> {
  return await send<Session>("POST", `/livegroups/${groupId}/create-session`, payload);
}

export async function joinSession(groupId: string, sessionId: string): Promise<Session> {
  return await send<Session>("POST", `/livegroups/${groupId}/sessions/${sessionId}/join-session`);
}

export interface LeaveSessionResponse {
  ok: boolean;
  deleted: boolean;
}

export async function leaveSession(groupId: string, sessionId: string): Promise<LeaveSessionResponse> {
  return await send<LeaveSessionResponse>("DELETE", `/livegroups/${groupId}/sessions/${sessionId}/leave-session`);
}

export async function guestJoinSession(sessionId: string, email: string): Promise<void> {
  await send<void>("POST", `/livegroups/sessions/${sessionId}/guest-join`, { email });
}

export async function guestJoinGroup(groupId: string, email: string): Promise<void> {
  await send<void>("POST", `/livegroups/${groupId}/guest-join`, { email });
}
